import {Component, inject, Input, OnInit} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {MaterialModule} from '../../../../material.module';

const GOOGLE_MERCHANT_TRANSFORMER = 'GoogleMerchantXmlTransformer';
export const GOOGLE_MERCHANT_GROUP_KEY_PROPERTIES = [
  'GroupKeySourceField',
  'GroupKeyExtractionPattern',
  'GroupKeyLegacyUnsafeValue'
] as const;

export function isValidGoogleMerchantGroupKeyPattern(pattern: string): boolean {
  const marker = '(?<product>';
  if (!pattern || pattern.length > 256 || !pattern.startsWith('\\A') || !pattern.endsWith('\\z') ||
      pattern.split(marker).length !== 2 || /\\(?:[1-9]|k<)/.test(pattern)) {
    return false;
  }

  let depth = 0;
  let inClass = false;
  let productCaptureEnd = -1;
  for (let index = 0; index < pattern.length; index += 1) {
    const character = pattern[index];
    if (character === '\\') {
      index += 1;
      continue;
    }
    if (character === '[') {
      inClass = true;
      continue;
    }
    if (character === ']') {
      inClass = false;
      continue;
    }
    if (inClass || character !== '(') {
      if (!inClass && character === ')') {
        depth -= 1;
        if (depth === 0 && productCaptureEnd < 0) productCaptureEnd = index;
        if (depth < 0) return false;
      }
      continue;
    }

    const remainder = pattern.slice(index);
    if (remainder.startsWith(marker)) {
      if (depth !== 0) return false;
      depth += 1;
      index += marker.length - 1;
      continue;
    }
    if (remainder.startsWith('(?:')) {
      depth += 1;
      index += 2;
      continue;
    }
    // .NET NonBacktracking rejects lookarounds, backreferences, conditionals and atomic groups;
    // plain or additional named captures would also violate the single-capture contract.
    return false;
  }

  if (depth !== 0 || productCaptureEnd < 0 || ['?', '*', '+', '{'].includes(pattern[productCaptureEnd + 1])) {
    return false;
  }

  try {
    new RegExp(`^(?:${pattern.slice(2, -2)})$`, 'u');
    return true;
  } catch {
    return false;
  }
}

export function validateTransformConfiguration(control: AbstractControl): ValidationErrors | null {
  const transformer = String(control.get('transformer')?.value ?? '');
  const primaryNode = String(control.get('primaryNode')?.value ?? '').trim();
  const properties = control.get('properties') as FormGroup | null;
  const errors: ValidationErrors = {};

  if (transformer && transformer !== GOOGLE_MERCHANT_TRANSFORMER && !primaryNode) {
    errors['primaryNodeRequired'] = true;
  }

  if (transformer === GOOGLE_MERCHANT_TRANSFORMER && properties) {
    const groupKeyEntries = Object.entries(properties.getRawValue())
      .filter(([key]) => key.toLowerCase().startsWith('groupkey'));

    if (groupKeyEntries.length > 0) {
      const configured = new Map(groupKeyEntries.map(([key, value]) => [key.toLowerCase(), String(value ?? '').trim()]));
      const hasCompleteGroupKeyConfiguration = GOOGLE_MERCHANT_GROUP_KEY_PROPERTIES.every(key =>
        configured.has(key.toLowerCase()) && !!configured.get(key.toLowerCase()));

      if (!hasCompleteGroupKeyConfiguration || groupKeyEntries.length !== GOOGLE_MERCHANT_GROUP_KEY_PROPERTIES.length) {
        errors['incompleteGoogleMerchantGroupKey'] = true;
      } else if (!['id', 'item_group_id'].includes(configured.get('groupkeysourcefield') ?? '')) {
        errors['invalidGoogleMerchantGroupKeySource'] = true;
      } else {
        const pattern = configured.get('groupkeyextractionpattern') ?? '';
        if (!isValidGoogleMerchantGroupKeyPattern(pattern)) {
          errors['invalidGoogleMerchantGroupKeyPattern'] = true;
        }
      }
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

interface TransformerOption {
  value: string;
  label: string;
  hint: string;
  properties: string[];
}


@Component({
  selector: 'app-transform-pattern',
  templateUrl: './transform-pattern.component.html',
  styleUrls: ['./transform-pattern.component.scss'],
    imports: [MaterialModule, ReactiveFormsModule],
  standalone: true
})
export class TransformPatternComponent implements OnInit {
    private fb = inject(FormBuilder);

  @Input() transformPatterForm!: FormGroup;
  @Input() allowGoogleMerchant = true;

  transformerOptions: TransformerOption[] = [
    {
      value: 'HtmlParseSinglePropertyTransformer',
      label: 'Single property',
      hint: 'Extract one value from a selected node.',
      properties: [
        'RegularExpression',
        'RegularExpressionReplace',
        'JsonObjectProperty',
        'NodeFilter',
        'RegularExpressionReplaceValue',
        'Attribute'
      ]
    },
    {
      value: 'HtmlParseFromAttributeTransformer',
      label: 'Attribute collection',
      hint: 'Build values from matching HTML attributes.',
      properties: ['AttributeFilter', 'DataCollectionAttribute', 'JsonAttributeFilterKey', 'JsonAttributeFilterValue']
    },
    {
      value: 'HtmlParseInnerTextTransformer',
      label: 'Inner text',
      hint: 'Extract normalized text from the selected node.',
      properties: []
    },
    {
      value: 'HtmlParseMinimisedTransformer',
      label: 'Minimized text',
      hint: 'Remove noisy HTML and extract compact text.',
      properties: []
    },
    {
      value: 'ImageUrlsFromHtmlTransformer',
      label: 'Image URLs from HTML',
      hint: 'Collect image src or data-src URLs below the selected node.',
      properties: []
    },
    {
      value: 'HtmlToMarkdownTransformer',
      label: 'HTML to Markdown',
      hint: 'Convert the selected HTML subtree to Markdown.',
      properties: []
    },
    {
      value: 'HtmlParseFromHtmlTableTransformer',
      label: 'HTML table',
      hint: 'Parse key/value style table content.',
      properties: []
    },
    {
      value: 'HtmlParseFromHtmlListTransformer',
      label: 'HTML list',
      hint: 'Parse list content with an optional description class.',
      properties: ['DescriptionClass']
    },
    {
      value: 'HtmlToSchemaOrgJsonLdTransformer',
      label: 'Schema.org JSON-LD',
      hint: 'Extract product data from JSON-LD script blocks.',
      properties: []
    },
    {
      value: 'GoogleMerchantXmlTransformer',
      label: 'Google Merchant XML',
      hint: 'Extract standard product fields from Google Merchant XML/RSS item feeds. Primary node is ignored.',
      properties: [...GOOGLE_MERCHANT_GROUP_KEY_PROPERTIES]
    }
  ];

  get availableTransformerOptions(): TransformerOption[] {
    return this.allowGoogleMerchant
      ? this.transformerOptions
      : this.transformerOptions.filter(option => option.value !== GOOGLE_MERCHANT_TRANSFORMER);
  }

  propertyDrafts: Record<string, { key: string; value: string }> = {};

  ngOnInit(): void {
    if (!this.transformPatterForm) {
      throw new Error('fieldMappingForm Input is required for FieldMappingComponent');
    }
  }

  get patterns(): FormArray {
    return this.transformPatterForm.get('patterns') as FormArray;
  }

  getTransformMapping(mapping: AbstractControl<any, any>): FormArray {
    return mapping.get('transforms') as FormArray;
  }

  getPropertiesGroup(transform: AbstractControl<any, any>): FormGroup {
    return transform.get('properties') as FormGroup;
  }

  /**
   * Removes a field mapping entry at the specified index.
   * @param index The index of the mapping to remove.
   */
  removeFieldMapping(index: number) {
    const mappings = this.transformPatterForm.get('patterns') as FormArray;
    mappings.removeAt(index);
  }


  createTransformPattern(): void {
    const group = this.fb.group({
      urlPattern: ['*', Validators.required],
      transforms: this.fb.array([])
    });

    this.patterns.push(group);
    this.transformPatterForm.markAsTouched();
  }

  createTransform(position: number): void {
    const group = this.fb.group({
      transformer: ['', Validators.required],
      name: ['', Validators.required],
      primaryNode: [''],
      properties: this.fb.group({}) // Initialize as FormGroup
    }, {validators: validateTransformConfiguration});

    const pattern = this.patterns.at(position) as FormGroup;
    const transforms = pattern.get('transforms') as FormArray;
    transforms.push(group);
    this.transformPatterForm.markAsTouched();
  }

  duplicatePattern(position: number): void {
    const source = this.patterns.at(position).value;
    this.patterns.insert(position + 1, this.createPatternGroupFromValue({
      ...source,
      urlPattern: `${source.urlPattern || '*'} copy`
    }));
    this.transformPatterForm.markAsTouched();
  }

  duplicateTransform(patternPosition: number, transformPosition: number): void {
    const pattern = this.patterns.at(patternPosition) as FormGroup;
    const transforms = pattern.get('transforms') as FormArray;
    const source = transforms.at(transformPosition).value;
    transforms.insert(transformPosition + 1, this.createTransformGroupFromValue({
      ...source,
      name: source.name ? `${source.name}_copy` : ''
    }));
    this.transformPatterForm.markAsTouched();
  }

  addCustomProperty(patternPosition: number, transformPosition: number): void {
    const draftId = this.getDraftId(patternPosition, transformPosition);
    const draft = this.propertyDrafts[draftId];
    const key = draft?.key?.trim();

    if (!key) {
      return;
    }

    this.addProperty(patternPosition, transformPosition, key, draft?.value ?? '');
    delete this.propertyDrafts[draftId];
  }

  addSuggestedProperty(patternPosition: number, transformPosition: number, key: string): void {
    this.addProperty(patternPosition, transformPosition, key, '');
  }

  addProperty(patternPosition: number, transformPosition: number, key: string, value: string): void {
    const pattern = this.patterns.at(patternPosition) as FormGroup;
    const transforms = pattern.get('transforms') as FormArray;
    const transform = transforms.at(transformPosition) as FormGroup;
    const properties = transform.get('properties') as FormGroup;

    if (!key || properties.contains(key)) {
      return;
    }

    properties.addControl(key, this.fb.control(value, Validators.required));
    transform.updateValueAndValidity();
    this.transformPatterForm.markAsTouched();
  }

  addGoogleMerchantGroupKeyConfiguration(patternPosition: number, transformPosition: number): void {
    for (const key of GOOGLE_MERCHANT_GROUP_KEY_PROPERTIES) {
      this.addProperty(patternPosition, transformPosition, key, key === 'GroupKeySourceField' ? 'id' : '');
    }
  }

  removeProperty(patternIndex: number, transformIndex: number, key: string): void {
    const pattern = this.patterns.at(patternIndex) as FormGroup;
    const transforms = pattern.get('transforms') as FormArray;
    const transform = transforms.at(transformIndex) as FormGroup;
    const properties = transform.get('properties') as FormGroup;

    properties.removeControl(key);
    transform.updateValueAndValidity();
    this.transformPatterForm.markAsTouched();
  }

  removeTransform(mappingIndex: number, transformIndex: number) {
    const mapping = this.patterns.at(mappingIndex) as FormGroup;
    const transforms = mapping.get('transforms') as FormArray;
    transforms.removeAt(transformIndex);
    this.transformPatterForm.markAsTouched();
  }

  getPropertyKeys(transform: AbstractControl): string[] {
    return Object.keys((transform.get('properties') as FormGroup).controls);
  }

  getTransformerOption(value: string | null | undefined): TransformerOption | undefined {
    return this.transformerOptions.find(option => option.value === value);
  }

  getSuggestedProperties(transform: AbstractControl): string[] {
    const selectedTransformer = transform.get('transformer')?.value;
    const existingProperties = new Set(this.getPropertyKeys(transform));
    return this.getTransformerOption(selectedTransformer)?.properties.filter(property => !existingProperties.has(property)) ?? [];
  }

  isGoogleMerchantTransform(transform: AbstractControl): boolean {
    return transform.get('transformer')?.value === GOOGLE_MERCHANT_TRANSFORMER;
  }

  hasAnyGoogleMerchantGroupKeyProperty(transform: AbstractControl): boolean {
    const keys = new Set(this.getPropertyKeys(transform).map(key => key.toLowerCase()));
    return GOOGLE_MERCHANT_GROUP_KEY_PROPERTIES.some(key => keys.has(key.toLowerCase()));
  }

  getTransformSummary(transform: AbstractControl): string {
    const selectedTransformer = transform.get('transformer')?.value;
    const option = this.getTransformerOption(selectedTransformer);
    const primaryNode = transform.get('primaryNode')?.value;

    return [option?.label ?? selectedTransformer, primaryNode].filter(Boolean).join(' - ') || 'Configure transform';
  }

  getDraftValue(patternPosition: number, transformPosition: number, key: 'key' | 'value'): string {
    return this.propertyDrafts[this.getDraftId(patternPosition, transformPosition)]?.[key] ?? '';
  }

  setDraftValue(patternPosition: number, transformPosition: number, key: 'key' | 'value', value: string): void {
    const draftId = this.getDraftId(patternPosition, transformPosition);
    const draft = {
      key: this.propertyDrafts[draftId]?.key ?? '',
      value: this.propertyDrafts[draftId]?.value ?? ''
    };
    draft[key] = value;
    this.propertyDrafts[draftId] = draft;
  }

  private getDraftId(patternPosition: number, transformPosition: number): string {
    return `${patternPosition}-${transformPosition}`;
  }

  private createPatternGroupFromValue(pattern: any): FormGroup {
    const group = this.fb.group({
      urlPattern: [pattern?.urlPattern ?? '*', Validators.required],
      transforms: this.fb.array([])
    });
    const transforms = group.get('transforms') as FormArray;
    (pattern?.transforms ?? []).forEach((transform: any) => transforms.push(this.createTransformGroupFromValue(transform)));
    return group;
  }

  private createTransformGroupFromValue(transform: any): FormGroup {
    const properties = this.fb.group({}) as FormGroup;
    Object.entries(transform?.properties ?? {}).forEach(([key, value]) => {
      properties.addControl(key, this.fb.control(value, Validators.required));
    });

    return this.fb.group({
      transformer: [transform?.transformer ?? '', Validators.required],
      name: [transform?.name ?? '', Validators.required],
      primaryNode: [transform?.primaryNode ?? ''],
      properties
    }, {validators: validateTransformConfiguration});
  }

}
